import { useMemo } from "react"
import Table, {
  createColumns,
  type ColumnMetaMap,
} from "@/v2-backoffice-client/components/table"
import TenantPrivacyFieldHeader from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyFieldHeader"
import TenantPrivacySourceCell from "@/v2-backoffice-client/tenant/privacy/TenantPrivacySourceCell"
import TenantPrivacyVisibilitySelect from "@/v2-backoffice-client/tenant/privacy/TenantPrivacyVisibilitySelect"
import { isPolicyFieldKey } from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.utils"
import type {
  PolicyFieldKey,
  PrivacyField,
  PrivacyPolicyRow,
  PrivacyVisibilityType,
} from "@/v2-backoffice-client/tenant/privacy/tenant.privacy.types"

export type TenantPrivacyPolicyTableProps = {
  rows: PrivacyPolicyRow[]
  fields: PrivacyField[]
  visibilityTypes: PrivacyVisibilityType[]
  isBusy: boolean
  onChangeField(sourceCode: number, key: PolicyFieldKey, code: number): void
}

// The source column carries a name plus a status line; the field columns
// each hold one xs Select. The Select can never overflow its cell at any
// column width — the trigger is `w-full` and its `[&>span]:line-clamp-1`
// lets the Select.Value span shrink while the chevron and the `shrink-0` dot
// keep their size — so the table's truncation popover never clones a live
// Select. 168px is the label budget instead: minus cell padding, the
// trigger's border + px-2, the chevron, the dot and its gap, ~104px of
// text-sm remains at the default cell padding (~88px at "Spacious"); a
// longer visibility-type name ellipsizes in every cell of that type.
const SOURCE_COLUMN_SIZE = 260
const FIELD_COLUMN_SIZE = 168

// The per-source grid on the shared Table: one row per data source that
// carries private data, one column per person field, both straight from the
// catalogue — a new source or renamed field shows up with no client change.
// Field descriptions ride the header's info icon; a field the person
// procedures do not enforce yet locks its cells (the header says so). An
// unsaved row is marked once, by the Unsaved pill in the source cell — the
// Table's active highlight is left alone because across the app it means
// "the row whose sheet is open".
function TenantPrivacyPolicyTable(props: TenantPrivacyPolicyTableProps) {
  const { rows, fields, visibilityTypes, isBusy, onChangeField } = props

  // The map is dynamic (one entry per catalogue field) and the cells close
  // over the options and handlers, so the column identity has to be memoised
  // on exactly those — Table memoises its derived column state on it.
  const columns = useMemo(() => {
    const fieldColumns: ColumnMetaMap<PrivacyPolicyRow> = {}
    const fieldByKey = new Map<string, PrivacyField>()

    for (const field of fields) {
      const key = field.ColumnName
      // Shields the grid from a ninth field arriving in the catalogue before
      // this client knows its column.
      if (!isPolicyFieldKey(key)) continue

      fieldByKey.set(key, field)
      fieldColumns[key] = {
        id: key,
        label: field.Name ?? key,
        description: field.Description,
        defaultSize: FIELD_COLUMN_SIZE,
        renderCell: ({ row }) => (
          <TenantPrivacyVisibilitySelect
            value={row.original.edits?.[key] ?? row.original[key]}
            visibilityTypes={visibilityTypes}
            disabled={!field.IsEnforced || isBusy}
            ariaLabel={`${row.original.ExternalSourceName ?? row.original.ExternalSourceCode} — ${field.Name ?? key}`}
            onChange={(code) => {
              onChangeField(row.original.ExternalSourceCode, key, code)
            }}
          />
        ),
      }
    }

    const columnDefs = createColumns<PrivacyPolicyRow>({
      enableSorting: false,
      enableHiding: false,
      columns: {
        ExternalSourceName: {
          id: "ExternalSourceName",
          label: "Data source",
          defaultSize: SOURCE_COLUMN_SIZE,
          renderCell: ({ row }) => (
            <TenantPrivacySourceCell row={row.original} />
          ),
        },
        ...fieldColumns,
      },
    })

    // The field headers sit above bordered Select triggers, whose content is
    // inset by the trigger's own box; the default header would put the label
    // 9px left of the dots. Swap in a header that shares that box — the
    // column keeps its meta (label, description) for everything else.
    // Assigned rather than spread: ColumnDef is a discriminated union and a
    // spread widens `id` back to optional.
    for (const columnDef of columnDefs) {
      const field = columnDef.id ? fieldByKey.get(columnDef.id) : undefined
      if (field) {
        columnDef.header = () => <TenantPrivacyFieldHeader field={field} />
      }
    }

    return columnDefs
  }, [fields, visibilityTypes, isBusy, onChangeField])

  return (
    <Table
      className="rounded-md"
      data={rows}
      columns={columns}
      getRowId={(row) => String(row.ExternalSourceCode)}
      enableRowSelection={false}
      enableSorting={false}
    >
      <Table.Content />
    </Table>
  )
}

export default TenantPrivacyPolicyTable
